import path from "node:path";
import { askInitQuestions } from "../prompts/init-prompts";
import { detectProjectType } from "../utils/detect-project";
import { logResult, logStep } from "../utils/logger";
import { publicPathToFile, resolveFromCwd } from "../utils/paths";
import { writeFileSafe } from "../utils/write-file-safe";
import { generateConfigFile } from "../generators/generate-config";
import { generateManifest } from "../generators/generate-manifest";
import { generateOfflinePage } from "../generators/generate-offline-page";
import { generateServiceWorker } from "../generators/generate-service-worker";
import { generateAssetReadme } from "../generators/generate-assets";
import type { GoOfflineConfig } from "../../src/types";

export async function runInitCommand(): Promise<void> {
  const projectType = detectProjectType();
  logStep(`Detected project type: ${projectType}`);

  const answers = await askInitQuestions();

  const starterConfig: GoOfflineConfig = {
    offlinePage: answers.offlinePagePath,
    swPath: answers.swPath,
    debug: answers.debug,
    pwa: answers.usePWA
      ? {
          name: answers.appName,
          shortName: answers.appName,
          themeColor: answers.themeColor,
          backgroundColor: "#ffffff",
          display: "standalone",
          startUrl: "/"
        }
      : false
  };

  const offlinePageFile = publicPathToFile(answers.publicDir, answers.offlinePagePath);
  const swFile = publicPathToFile(answers.publicDir, answers.swPath);
  const manifestFile = path.resolve(process.cwd(), answers.publicDir, "manifest.webmanifest");
  const configFile = resolveFromCwd("go-offline.config.ts");
  const assetsReadmeFile = path.resolve(process.cwd(), answers.publicDir, "go-offline-assets.txt");

  const offlineStatus = await writeFileSafe(offlinePageFile, generateOfflinePage(answers.appName));
  logResult(path.relative(process.cwd(), offlinePageFile), offlineStatus);

  const swStatus = await writeFileSafe(swFile, generateServiceWorker(starterConfig));
  logResult(path.relative(process.cwd(), swFile), swStatus);

  if (answers.usePWA) {
    const manifestStatus = await writeFileSafe(
      manifestFile,
      generateManifest(
        typeof starterConfig.pwa === "object" && starterConfig.pwa
          ? starterConfig.pwa
          : { name: answers.appName }
      )
    );
    logResult(path.relative(process.cwd(), manifestFile), manifestStatus);

    const assetsStatus = await writeFileSafe(assetsReadmeFile, generateAssetReadme());
    logResult(path.relative(process.cwd(), assetsReadmeFile), assetsStatus);
  }

  if (answers.includeConfig) {
    const configStatus = await writeFileSafe(configFile, generateConfigFile(starterConfig));
    logResult(path.relative(process.cwd(), configFile), configStatus);
  }

  logStep("Starter setup complete.");
  logStep('Next step: import `setupOffline()` or wrap your app with `GoOfflineProvider`.');
}
